/**
 * MBA Lab — the analytics PII guard.
 *
 * Pure, DOM-free, and deliberately separate from src/analytics.js so it can be unit-tested without
 * a browser. Everything here fails CLOSED: what it does not recognise, it drops.
 *
 * Two layers, because either alone is insufficient:
 *  1. KEYS — only params declared for that event in src/analytics-events.js survive. This is what
 *     stops a new call site from inventing `search_query` and shipping it to Google.
 *  2. VALUES — even on an allowed key, anything that looks like free text, an email, a URL, or a
 *     long string is dropped. A key allow-list alone does nothing about a raw query smuggled in
 *     under an innocuous name like `mode_id`, and that is the likelier mistake.
 */

import { allowedParamsFor, isRegisteredEvent } from './analytics-events.js';

/**
 * GA4 truncates param values at 100 characters anyway. We cut at 64: anything longer than that is
 * prose, and prose is how user input leaks.
 */
export const MAX_PARAM_VALUE_LENGTH = 64;

/** GA4's own hard limit on a param value. */
export const GA4_MAX_PARAM_VALUE_LENGTH = 100;

/**
 * Params whose values are server-authored prose rather than user input, so the free-text heuristic
 * is a category error on them. These are truncated to GA4's limit instead of being dropped.
 *
 * page_title is the whole list today, and it has to be here: real page titles routinely run past 64
 * characters ("MBA Programs at the University of Virginia | Cost, Salary..."), so the heuristic
 * dropped it on every single page and logged an error while doing so.
 */
export const TEXT_PARAMS = Object.freeze(['page_title']);

/**
 * Key name tokens that must never appear, even if someone adds them to the registry. This is the
 * backstop that catches a well-meaning future edit to analytics-events.js - the registry is the
 * gate, this is the lock on the gate.
 *
 * Matched against the snake_case-split tokens of the key, NOT as a substring. Substring matching
 * looks equivalent and is not: `address` would swallow `addressed_count`, a legitimate coarse count
 * from the change-readiness tool. Regex word boundaries do not help here either, because `\w`
 * includes the underscore, so `\bemail\b` fails to match `email_address`.
 */
export const FORBIDDEN_KEY_TOKENS = Object.freeze([
  'salary', 'tuition', 'debt', 'scholarship', 'income', 'earnings', 'cost', 'price',
  'email', 'phone', 'address', 'password', 'token', 'secret',
  'query', 'term', 'freetext', 'text', 'note', 'comment', 'answer', 'message',
]);

/** @returns {boolean} whether any snake_case token of `key` is forbidden. */
export function hasForbiddenKeyToken(key) {
  return String(key)
    .split('_')
    .some((token) => FORBIDDEN_KEY_TOKENS.indexOf(token.toLowerCase()) !== -1);
}

/** Values that are almost certainly user input or an identifier rather than an enum. */
function isSuspiciousValue(value) {
  if (typeof value !== 'string') return false;
  if (value.length > MAX_PARAM_VALUE_LENGTH) return true;
  if (value.indexOf('@') !== -1) return true; // email
  if (/https?:\/\//i.test(value)) return true; // absolute URL - may carry a query string
  if (value.indexOf('?') !== -1) return true; // any query string
  if (/\d{5,}/.test(value)) return true; // phone / long id
  return false;
}

/**
 * Strips everything not explicitly allowed for this event.
 *
 * @param {string} eventName
 * @param {Object} [params]
 * @returns {{ registered: boolean, params: Object, problems: string[] }}
 *   `registered` is false for an unknown event, which callers should treat as "do not send".
 *   `problems` is a human-readable list for the caller to surface (throw in dev, log in prod).
 */
export function sanitizeParams(eventName, params) {
  const problems = [];

  if (!isRegisteredEvent(eventName)) {
    problems.push(
      `"${eventName}" is not in the event registry (src/analytics-events.js). Add it there first.`
    );
    return { registered: false, params: {}, problems };
  }

  const allowed = allowedParamsFor(eventName);
  const clean = {};

  Object.keys(params || {}).forEach((key) => {
    const value = params[key];

    if (hasForbiddenKeyToken(key)) {
      problems.push(`"${eventName}" param "${key}" is a forbidden key name and was dropped.`);
      return;
    }

    if (allowed.indexOf(key) === -1) {
      problems.push(
        `"${eventName}" param "${key}" is not declared for this event and was dropped.`
      );
      return;
    }

    if (value === null || value === undefined) return;

    if (TEXT_PARAMS.indexOf(key) !== -1) {
      clean[key] = typeof value === 'string'
        ? value.slice(0, GA4_MAX_PARAM_VALUE_LENGTH)
        : value;
      return;
    }

    if (isSuspiciousValue(value)) {
      problems.push(
        `"${eventName}" param "${key}" looks like free text, an email, or a URL, and was dropped.`
      );
      return;
    }

    clean[key] = value;
  });

  return { registered: true, params: clean, problems };
}
